import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuth } from './../util/auth';
import { getListsByUsername } from '../util/util';
import {
  getFollowingOrgsByUser,
  getFollowingShowsDataByUser,
  getSeenShowRowsForHeatmap,
  getSeenShowsCountByUser,
  getSeenShowsDataByUserPaginated,
  getWantToSeeDataByUser,
} from '../util/db';
import { Link, useRouter } from '../util/router';
import { formatJoinDate } from '../util/data-factory';
import ProfilePhoto from './ProfilePhoto';
import SeenShowsHeatmap from './SeenShowsHeatmap';
import HomeFooter from './HomeFooter';
import { MobileMenuButton } from './shell/AppShell';
import showrunnerLogo from '../assets/images/SHOWRUNNER.png';

const LIST_COVER_FRAME = 'rounded-lg shadow-xl overflow-hidden relative aspect-[6/7] w-full';

/** Navy + centered white logo when a list has no cover image. */
function EmptyListCoverArt() {
  return (
    <div className="absolute inset-0 bg-regal-blue flex items-center justify-center p-[10%]">
      <img
        src={showrunnerLogo}
        alt=""
        className="max-h-full max-w-full w-full object-contain object-center brightness-0 invert opacity-95 pointer-events-none select-none"
        aria-hidden
      />
    </div>
  );
}

function ListCard({ to, cover, title, meta, isPrivate }) {
  return (
    <Link
      to={to}
      className="block w-full text-inherit no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-red/40 focus-visible:ring-offset-2 rounded-lg"
    >
      <div className={LIST_COVER_FRAME}>
        {cover ? (
          <img className="absolute inset-0 w-full h-full object-cover" alt="" src={cover} />
        ) : (
          <EmptyListCoverArt />
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0 text-sm md:text-base text-slate-900 uppercase leading-tight truncate">
          {title}
        </div>
        {isPrivate && <LockClosedIcon className="w-4 shrink-0 text-slate-500" />}
      </div>
      {meta && <p className="text-xs md:text-sm text-slate-400">{meta}</p>}
    </Link>
  );
}

function Profile() {
  const auth = useAuth();
  const router = useRouter();
  const { username } = router.query;
  const [userData, setUserData] = useState({});
  const [userLists, setUserLists] = useState([]);
  const [recentSeen, setRecentSeen] = useState([]);
  const [seenCount, setSeenCount] = useState(0);
  const [heatmapRows, setHeatmapRows] = useState([]);
  const [wantToSee, setWantToSee] = useState([]);
  const [followingShows, setFollowingShows] = useState([]);
  const [followingOrgs, setFollowingOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const isOwner = !!auth.user && auth.user.username === username;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { userData, userLists } = await getListsByUsername(username);
        if (cancelled) return;
        setUserData(userData);
        setUserLists(userLists);

        const [seen, count, heat, wts, fShows, fOrgs] = await Promise.all([
          getSeenShowsDataByUserPaginated(userData.id, 20, 0),
          getSeenShowsCountByUser(userData.id),
          getSeenShowRowsForHeatmap(userData.id),
          getWantToSeeDataByUser(userData.id),
          getFollowingShowsDataByUser(userData.id),
          getFollowingOrgsByUser(userData.id),
        ]);
        if (cancelled) return;
        setRecentSeen(seen);
        setSeenCount(count);
        setHeatmapRows(heat);
        setWantToSee(wts);
        setFollowingShows(fShows);
        setFollowingOrgs(fOrgs);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  const visibleLists = (isOwner ? userLists : userLists.filter(list => !list.is_private)).sort(
    (a, b) => new Date(b.modified_at || 0) - new Date(a.modified_at || 0)
  );

  return (
    <div className="w-full">
      <div className="w-full rounded-b-[33px] bg-slate-100 px-4 pb-6 pt-4 md:rounded-none md:px-8 md:pt-8">
        <div className="max-w-[90rem] mx-auto">
          <div className="flex justify-end md:hidden">
            <MobileMenuButton />
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-10">
            <div className="flex-none">
              <ProfilePhoto userId={userData.id} isSettings={false} />
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center justify-between gap-3">
                <div className="text-2xl md:text-4xl font-semibold text-secondary-red truncate">
                  @{userData.username}
                </div>
                {isOwner && (
                  <Link
                    to="/settings/general"
                    aria-label="Account settings"
                    className="rounded-lg p-2 hover:bg-black/5"
                  >
                    <Cog6ToothIcon className="h-7 w-7" />
                  </Link>
                )}
              </div>
              {userData.name && (
                <div className="mt-1 text-lg md:text-2xl text-black/60">{userData.name}</div>
              )}
              {userData.about && (
                <div className="mt-2 max-w-2xl text-sm md:text-lg text-black/90">
                  {userData.about}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm md:text-base">
                <Link to={`/following/${userData.id}`} className="hover:underline">
                  <strong>{followingOrgs.length}</strong>{' '}
                  <span className="text-black/60">Following</span>
                </Link>
                <Link to={`/seen-shows/${userData.id}`} className="hover:underline">
                  <strong>{seenCount}</strong> <span className="text-black/60">Seen</span>
                </Link>
                <span>
                  <strong>{visibleLists.length}</strong>{' '}
                  <span className="text-black/60">Curated lists</span>
                </span>
                <span>
                  <span className="text-black/60">Joined</span>{' '}
                  {formatJoinDate(userData.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="min-h-[calc(100vh-190px)] bg-white">
        <div className="w-full max-w-[90rem] mx-auto px-4 md:px-8 pt-8">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="text-lg md:text-2xl font-semibold">Recently Seen Shows</h2>
            {recentSeen.length > 0 && (
              <Link
                to={`/seen-shows/${userData.id}`}
                className="text-sm md:text-base font-semibold text-secondary-red hover:underline"
              >
                See more
              </Link>
            )}
          </div>

          {loading ? (
            <div className="py-6 flex justify-center">
              <span className="loading loading-spinner loading-md text-secondary-red" />
            </div>
          ) : (
            <>
              {heatmapRows.length > 0 && (
                <div className="mt-3">
                  <SeenShowsHeatmap data={heatmapRows} />
                </div>
              )}
              {recentSeen.length ? (
                <div className="flex gap-4 overflow-x-auto pb-4 pt-3 no-scrollbar">
                  {recentSeen.map(show => (
                    <Link
                      key={show.show_id}
                      to={`/show/${show.show_id}`}
                      className="shrink-0 w-[96px] md:w-[140px] group"
                    >
                      <div className="w-[96px] h-[96px] md:w-[140px] md:h-[140px] rounded-lg overflow-hidden bg-slate-200 shadow-md">
                        {show.image_urls?.[0] ? (
                          <img
                            src={show.image_urls[0]}
                            alt=""
                            className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-black/40 p-1 text-center">
                            No image
                          </div>
                        )}
                      </div>
                      <p className="text-[11px] md:text-xs leading-tight mt-1.5 text-center text-black/80 line-clamp-2">
                        {show.display_title}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-black/50 py-4 max-w-xl">
                  No seen shows yet. Find shows in the{' '}
                  <Link to="/feed" className="text-secondary-red underline">
                    feed
                  </Link>{' '}
                  and mark them as seen to build a history here.
                </p>
              )}
            </>
          )}
        </div>

        <div className="w-full max-w-[90rem] px-4 pt-8 pb-10 md:px-8 mx-auto">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-lg md:text-2xl font-semibold">
              {isOwner ? 'My Lists' : 'Public Lists'}
            </h2>
            {isOwner && (
              <Link
                to="/my-lists"
                className="text-sm md:text-base font-semibold text-secondary-red hover:underline"
              >
                Manage
              </Link>
            )}
          </div>
          {userData?.id && (
            <div className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5 sm:gap-6 md:gap-7 lg:gap-8">
              <ListCard
                to={`/want-to-see/${userData.id}`}
                cover={wantToSee[0]?.image_urls?.[0]}
                title="Want to See"
                meta={`${wantToSee.length} saved`}
              />
              <ListCard
                to={`/seen-shows/${userData.id}`}
                cover={recentSeen[0]?.image_urls?.[0]}
                title="Seen Shows"
                meta={`${seenCount} visited`}
              />
              {isOwner && (
                <ListCard
                  to={`/following-shows/${userData.id}`}
                  cover={followingShows[0]?.image_urls?.[0]}
                  title="Following Shows"
                  meta={`${followingShows.length} shows`}
                />
              )}
              {visibleLists.map(list => (
                <ListCard
                  key={list.id}
                  to={`/lists/${list.id}`}
                  cover={list.first_show_data?.image_urls?.[0]}
                  title={list.list_name}
                  meta={`${list.shows.length} saved`}
                  isPrivate={list.is_private}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      <HomeFooter />
    </div>
  );
}

export default Profile;
